import { SelectionProviderChildProps } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { Messages } from '../../../gen-src/Localize';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FieldsGridSortProvider } from '../sorting/sortProviders/FieldsGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { SheetsGridSortProvider } from '../sorting/sortProviders/SheetsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { VirtualConnectionLineageCounts } from './VirtualConnectionCountsQueryWrapper';
import { VirtualConnectionDownstreamDashboardsQueryGrid } from './VirtualConnectionDownstreamDashboardsQueryGrid';
import { VirtualConnectionDownstreamDatasourcesQueryGrid } from './VirtualConnectionDownstreamDatasourcesQueryGrid';
import { VirtualConnectionDownstreamFlowsQueryGrid } from './VirtualConnectionDownstreamFlowsQueryGrid';
import { VirtualConnectionDownstreamMetricsQueryGrid } from './VirtualConnectionDownstreamMetricsQueryGrid';
import { VirtualConnectionDownstreamOwnersPane } from './VirtualConnectionDownstreamOwnersPane';
import { VirtualConnectionDownstreamSheetsQueryGrid } from './VirtualConnectionDownstreamSheetsQueryGrid';
import { VirtualConnectionDownstreamWorkbooksQueryGrid } from './VirtualConnectionDownstreamWorkbooksQueryGrid';
import { VirtualConnectionTablesQueryGrid } from './VirtualConnectionTablesQueryGrid';
import { VirtualConnectionUpstreamDatabasesQueryGrid } from './VirtualConnectionUpstreamDatabasesQueryGrid';
import { VirtualConnectionUpstreamTablesQueryGrid } from './VirtualConnectionUpstreamTablesQueryGrid';

export type VirtualConnectionLineageItemConfig = LineageItemConfig<VirtualConnectionLineageCounts, VirtualConnectionLineagePaneParams>;
export type VirtualConnectionLineageSection = LineageSection<VirtualConnectionLineageCounts, VirtualConnectionLineagePaneParams>;

export type VirtualConnectionLineagePaneParams = SelectionProviderChildProps & {
  readonly title: string;
  readonly vizportalUrlId: string;
  readonly filteredTableId: string | null;
  readonly count: number | null;
};

const VirtualConnectionUpstreamItems: ReadonlyArray<VirtualConnectionLineageItemConfig> = [
  {
    lineageItemType: LineageItemType.Database,
    messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
    messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
    count: counts => counts.upstreamDatabases,
    path: (urls, id) => urls.virtualConnectionLineageUpstreamDatabasesPath(id),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DatabasesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <VirtualConnectionUpstreamDatabasesQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              filteredTableId={paneParams.filteredTableId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </DatabasesGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Table,
    messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
    messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
    count: counts => counts.upstreamTables,
    path: (urls, vizportalUrlId) => urls.virtualConnectionLineageUpstreamTablesPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <TablesGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <VirtualConnectionUpstreamTablesQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              filteredTableId={paneParams.filteredTableId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />
          }
        </TablesGridSortProvider>
      </PaneWithTitle>
    ),
  },
];

const VirtualConnectionAnchor: VirtualConnectionLineageItemConfig = {
  lineageItemType: LineageItemType.VirtualConnection,
  messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
  messagesForPane: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
  count: counts => counts.tables,
  path: (urls, id) => urls.virtualConnectionLineageVirtualConnectionTablesPath(id),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <FieldsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <VirtualConnectionTablesQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            selectedKeys={paneParams.selectedKeys}
            updateSelectionForKey={paneParams.updateSelectionForKey}
            rowCount={paneParams.count}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
          />
        }
      </FieldsGridSortProvider>
    </PaneWithTitle>
  ),
};

function getVirtualConnectionDownstreamItems(): ReadonlyArray<VirtualConnectionLineageItemConfig> {
  return [
    {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.DownstreamFlowsWithCount, withoutCount: Messages.DownstreamFlows },
      count: counts => counts.downstreamFlows,
      path: (urls, vizportalUrlId) => urls.virtualConnectionLineageDownstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionDownstreamFlowsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.DownstreamDatasourcesWithCount, withoutCount: Messages.DownstreamDatasources },
      count: counts => counts.downstreamDatasources,
      path: (urls, id) => urls.virtualConnectionLineageDownstreamDatasourcesPath(id),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionDownstreamDatasourcesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      )
    },
    {
      lineageItemType: LineageItemType.Workbook,
      messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
      messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
      count: counts => counts.downstreamWorkbooks,
      path: (urls, vizportalUrlId) => urls.virtualConnectionLineageDownstreamWorkbooksPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <WorkbooksGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionDownstreamWorkbooksQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </WorkbooksGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Sheet,
      messagesForLineage: { withCount: Messages.SheetsWithCount, withoutCount: Messages.Sheets },
      messagesForPane: { withCount: Messages.DownstreamSheetsWithCount, withoutCount: Messages.DownstreamSheets },
      count: counts => counts.downstreamSheets,
      path: (urls, vizportalUrlId) => urls.virtualConnectionLineageDownstreamSheetsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <SheetsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionDownstreamSheetsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </SheetsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Dashboard,
      messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
      messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
      count: counts => counts.downstreamDashboards,
      path: (urls, vizportalUrlId) => urls.virtualConnectionLineageDownstreamDashboardsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DashboardsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionDownstreamDashboardsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DashboardsGridSortProvider>
        </PaneWithTitle>
      ),
    },
    {
      lineageItemType: LineageItemType.Metric,
      messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
      messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
      count: counts => counts.downstreamMetrics,
      path: (urls, vizportalUrlId) => urls.virtualConnectionLineageDownstreamMetricsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <MetricsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <VirtualConnectionDownstreamMetricsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                filteredTableId={paneParams.filteredTableId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </MetricsGridSortProvider>
        </PaneWithTitle>
      ),
    }
  ];
}
const memoizedGetVirtualConnectionDownstreamItems = defaultMemoize(getVirtualConnectionDownstreamItems);

const VirtualConnectionOwners: VirtualConnectionLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, id) => urls.virtualConnectionLineageDownstreamOwnersPath(id),
  paneFactory: (paneParams) => (
    <VirtualConnectionDownstreamOwnersPane
      title={paneParams.title}
      vizportalUrlId={paneParams.vizportalUrlId}
      filteredTableId={paneParams.filteredTableId}
      rowCount={paneParams.count}
    />
  ),
};

function getVirtualConnectionLineageConfiguration(): ReadonlyArray<VirtualConnectionLineageSection> {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: VirtualConnectionUpstreamItems,
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [VirtualConnectionAnchor],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetVirtualConnectionDownstreamItems(),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [VirtualConnectionOwners],
    },
  ];
}
export const memoizedGetVirtualConnectionLineageConfiguration = defaultMemoize(getVirtualConnectionLineageConfiguration);
